// src/components/blog/TrackHolidayGiftSpending.js

import React from 'react';
import { Box, Typography, Container, Button } from '@mui/material';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import blogHeroImage from '../../assets/blog-gift-spending.png';

function TrackHolidayGiftSpending() {
  const structuredData = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "Article",
        "headline": "Track Holiday Gift Spending by Person & Event",
        "description": "Stop using spreadsheets to track holiday gift spending. ListLink's free dashboard shows exactly what you spent on each person and event. Free.",
        "author": { "@type": "Organization", "name": "ListLink" },
        "publisher": { "@type": "Organization", "name": "ListLink" },
        "url": "https://list-link.com/blog/track-holiday-gift-spending",
        "datePublished": "2026-02-22",
        "dateModified": "2026-02-22",
        "image": "https://list-link.com/og-image.png"
      },
      {
        "@type": "FAQPage",
        "mainEntity": [
          {
            "@type": "Question",
            "name": "Is the gift spending tracker free?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "Yes, completely free. ListLink's spending dashboard is included with no subscription or premium tier required."
            }
          },
          {
            "@type": "Question",
            "name": "How does ListLink track gift spending automatically?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "When you mark a gift as purchased on someone's wish list, ListLink prompts you to enter the price and records it. Your dashboard updates with your spending as you go."
            }
          },
          {
            "@type": "Question",
            "name": "Can I track spending across multiple events?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "Yes. The dashboard breaks spending down by event (Christmas, Sinterklaas, birthdays), by person, and by month — so you can compare across all your gift-giving occasions."
            }
          },
          {
            "@type": "Question",
            "name": "Does any other gift list app have a spending dashboard?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "No major competitor does. Elfster, Giftster, and Amazon Wishlist all track purchases, but none offer a spending breakdown by recipient or event. This is unique to ListLink."
            }
          }
        ]
      }
    ]
  };

  return (
    <div>
      <Helmet>
        <title>Track Holiday Gift Spending by Person & Event | ListLink</title>
        <meta
          name="description"
          content="Stop using spreadsheets to track holiday gift spending. ListLink's free dashboard shows exactly what you spent on each person and event. Free."
        />
        <meta
          name="keywords"
          content="gift spending tracker, holiday gift budget, track gift expenses, gift budget app, holiday spending dashboard, gift expense tracker free, gift budget per person"
        />
        <meta property="og:title" content="Track Holiday Gift Spending by Person & Event | ListLink" />
        <meta property="og:description" content="Track gift spending by person, event, and month with a free dashboard. No more spreadsheets. Free." />
        <meta property="og:url" content="https://list-link.com/blog/track-holiday-gift-spending" />
        <meta property="og:type" content="article" />
        <meta property="og:image" content="https://list-link.com/og-image.png" />
        <meta name="twitter:card" content="summary_large_image" />
        <link rel="canonical" href="https://list-link.com/blog/track-holiday-gift-spending" />
        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
      </Helmet>

      {/* Hero */}
      <Box sx={{ backgroundColor: '#FDFAF5', pt: 6, pb: 4 }}>
        <Container maxWidth="md">
          <Typography variant="caption" sx={{ color: '#8A7968', fontFamily: '"Plus Jakarta Sans", sans-serif' }}>
            February 22, 2026
          </Typography>
          <Typography variant="h1" component="h1" gutterBottom sx={{ fontSize: { xs: '2rem', md: '2.8rem' }, mt: 1 }}>
            Track Holiday Gift Spending by Person & Event
          </Typography>
          <Typography variant="h6" sx={{ color: '#5C4F3D', fontFamily: '"Plus Jakarta Sans", sans-serif', fontWeight: 400, lineHeight: 1.6, mb: 4 }}>
            You bought gifts for 15 people across 3 events this year. Do you know what you actually spent? On each person?
          </Typography>
          <Box
            component="img"
            src={blogHeroImage}
            alt="Holiday gift spending tracker with notebook and festive decorations"
            sx={{
              width: '100%',
              height: { xs: 220, md: 320 },
              objectFit: 'cover',
              objectPosition: 'center 40%',
              display: 'block',
              borderRadius: 3,
            }}
          />
        </Container>
      </Box>

      {/* Content */}
      <Container maxWidth="md" sx={{ py: 6 }}>
        <Typography variant="body1" paragraph>
          Every holiday season, the same thing happens. You buy gifts for family, friends, coworkers, and kids' teachers. By January, you have no idea what you actually spent, whether you were fair across people, or how much you went over budget. Some people track this in spreadsheets — but most just guess and hope for the best.
        </Typography>

        <Typography variant="h4" component="h2" gutterBottom sx={{ mt: 4 }}>
          The Problem with Spreadsheets
        </Typography>
        <Typography variant="body1" paragraph>
          Spreadsheets require manual data entry after every purchase. You have to remember to open the file, add the amount, categorize it by person and event, and keep it updated in real time. In practice, nobody does this consistently. By the third event of the year, the spreadsheet is abandoned.
        </Typography>
        <Typography variant="body1" paragraph>
          Dedicated budget apps like YNAB or Mint can track overall spending categories, but they don't break down gift spending by recipient or event. They'll tell you that you spent $800 on "gifts" but not that you spent $200 on your sister and $50 on your nephew.
        </Typography>

        <Typography variant="h4" component="h2" gutterBottom sx={{ mt: 4 }}>
          What a Gift Spending Dashboard Should Do
        </Typography>
        <Typography variant="body1" component="div" paragraph>
          <ul>
            <li><strong>Track by person</strong> — See total spending on each recipient across all events.</li>
            <li><strong>Track by event</strong> — Know how much Christmas cost vs. birthdays vs. Sinterklaas.</li>
            <li><strong>Track by month</strong> — Understand your spending patterns and plan ahead.</li>
            <li><strong>Integrated</strong> — When you mark a gift as purchased in your gift list, you enter the price and it records to your dashboard automatically.</li>
          </ul>
        </Typography>

        <Typography variant="h4" component="h2" gutterBottom sx={{ mt: 4 }}>
          How ListLink Solves This
        </Typography>
        <Typography variant="body1" paragraph>
          <Link to="/">ListLink</Link> has a built-in purchases and spending dashboard that tracks every gift you buy. When you mark an item as purchased on someone's wish list, you enter the price and it records to your dashboard. You can then view your spending broken down by person, event, or time period.
        </Typography>
        <Typography variant="body1" paragraph>
          This means the same app you use to browse wish lists and coordinate gift purchases also handles your budget tracking. No separate app, no spreadsheet — it's part of your gift-giving workflow. <Link to="/learn-more">See how all of ListLink's features work together</Link>.
        </Typography>
        <Typography variant="body1" paragraph>
          This feature is what makes ListLink different from every other gift list app on the market. Elfster, Giftster, Giftwhale, and Amazon Wishlist all help you create and share wish lists — but none of them tell you what you spent afterwards. ListLink does.
        </Typography>

        <Typography variant="h4" component="h2" gutterBottom sx={{ mt: 4 }}>
          A Better Way to Budget for Gifts
        </Typography>
        <Typography variant="body1" paragraph>
          Instead of setting a vague "gift budget" at the start of the year, use ListLink to track your actual spending as it happens. After your first event, you'll have real data. After a full year, you'll know exactly where your money went and can plan accordingly.
        </Typography>
        <Typography variant="body1" paragraph>
          It's free to use, and you can get started in under a minute.
        </Typography>

        {/* FAQ Section */}
        <Typography variant="h4" component="h2" gutterBottom sx={{ mt: 6 }}>
          Frequently Asked Questions
        </Typography>

        <Box sx={{ mt: 2 }}>
          <Typography variant="h6" component="h3" gutterBottom>
            Is the gift spending tracker free?
          </Typography>
          <Typography variant="body1" paragraph>
            Yes, completely free. ListLink's spending dashboard is included with no subscription or premium tier required.
          </Typography>

          <Typography variant="h6" component="h3" gutterBottom>
            How does ListLink track gift spending automatically?
          </Typography>
          <Typography variant="body1" paragraph>
            When you mark a gift as purchased on someone's wish list, ListLink prompts you to enter the price and records it. Your dashboard updates with your spending as you go.
          </Typography>

          <Typography variant="h6" component="h3" gutterBottom>
            Can I track spending across multiple events?
          </Typography>
          <Typography variant="body1" paragraph>
            Yes. The dashboard breaks spending down by event (Christmas, Sinterklaas, birthdays), by person, and by month — so you can compare across all your gift-giving occasions.
          </Typography>

          <Typography variant="h6" component="h3" gutterBottom>
            Does any other gift list app have a spending dashboard?
          </Typography>
          <Typography variant="body1" paragraph>
            No major competitor does. Elfster, Giftster, and Amazon Wishlist all track purchases, but none offer a spending breakdown by recipient or event. This is unique to ListLink.
          </Typography>
        </Box>

        {/* CTA */}
        <Box sx={{ mt: 6, p: 4, backgroundColor: '#0079bf', borderRadius: 2, textAlign: 'center' }}>
          <Typography variant="h5" sx={{ color: '#fff', mb: 2 }}>
            Start tracking your gift spending today
          </Typography>
          <Button
            variant="contained"
            color="secondary"
            size="large"
            component={Link}
            to="/register"
          >
            Sign Up Free
          </Button>
        </Box>

        <Box sx={{ mt: 4 }}>
          <Button component={Link} to="/blog" color="primary">
            &larr; Back to Blog
          </Button>
        </Box>
      </Container>
    </div>
  );
}

export default TrackHolidayGiftSpending;
